import { Close } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import FileCirclePlusRed from '../../assets/images/file-circle-plus-red.svg';
import FileCirclePlus from '../../assets/images/file-circle-plus.svg';
import { isAqueductEnabled } from '../../auth/utils';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item';
import { InputType } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { fileExtension, fileMaxSize } from '../../common/hooks/use-form-item/validators/operational';
import { BaseRESTService } from '../../common/services/BaseRESTService';
import { prettyBytes } from '../../common/utils/ui';
import { Report } from '../models/Report';
import { ReportService } from '../services/ReportService';

enum TraceUploadStatus {
  READY = 'ready',
  UPLOADING = 'uploading',
  ERROR = 'error',
}

const ProgressRing = (props: { progress: number | null }) => {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
      <CircularProgress
        variant={props.progress !== null ? 'determinate' : 'indeterminate'}
        size={100}
        thickness={3}
        value={props.progress !== null ? props.progress : undefined}
      />
      {props.progress !== null && (
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography>{`${props.progress}%`}</Typography>
        </Box>
      )}
    </Box>
  );
};

const FileEntry = (props: { file: File; error?: string | null; onRemove?: () => void; onRetry?: () => void }) => {
  const svgStyle = { height: '50', width: '57', viewBox: '0 0 80 70', fill: 'white', style: { flexShrink: '0' } };

  return (
    <Box className="trace-upload-entry" sx={{ display: 'flex', alignItems: 'center', py: 1, pr: 0, pl: 2 }}>
      {props.error && <FileCirclePlusRed role="img" title={t('a11y.dialogs.fileUploadError')} {...svgStyle} />}
      {!props.error && <FileCirclePlus role="img" title={t('a11y.dialogs.fileUploadProgress')} {...svgStyle} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: '1', py: 0, px: 2 }}>
        <Typography variant="body1">{props.file.name}</Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          <Typography component="span" variant="body2">
            {prettyBytes(props.file.size)}
          </Typography>
          <Typography component="span" variant="body2" sx={{ py: 0, px: 1 }}>
            &#x2022;
          </Typography>
          {props.error ? (
            <Typography component="span" variant="body2" sx={{ color: 'error.main' }}>
              {props.error}
            </Typography>
          ) : (
            <Typography component="span" variant="body2">
              {t('traceUploadForm.uploading')}
            </Typography>
          )}
        </Typography>
      </Box>
      {props.error && props.onRetry && (
        <Button variant="contained" color="light-grey" size="small" onClick={props.onRetry} sx={{ flexShrink: '0', mr: 1.5 }}>
          {t('traceUploadForm.retry')}
        </Button>
      )}
      {props.onRemove && (
        <IconButton title={t('a11y.dialogs.fileUploadRemove')} onClick={props.onRemove}>
          <Close />
        </IconButton>
      )}
    </Box>
  );
};

export const TraceForm = (props: { projectId: number; slotId: number; showCapacityWarning?: boolean; onDone: (report: Report) => void }) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [reportService] = useState<ReportService>(new ReportService());
  const [restService] = useState<BaseRESTService>(new BaseRESTService());

  const { onDone, showCapacityWarning, slotId, projectId } = props;
  const [status, setStatus] = useState<TraceUploadStatus>(TraceUploadStatus.READY);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [file, fileComponent, , fileError, setFile] = useFormItem<File | null>({
    type: InputType.TYPE_FILE_DRAG_DROP,
    label: t('traceUploadForm.restrictions'),
    value: null,
    validator: and(fileMaxSize(4, 'GB'), fileExtension(['.gz', '.cst.gz', '.cst', '.pcap', '.pcapng'])),
  });

  useEffect(() => {
    // Cancel all active requests on unmount
    return () => {
      restService.cancelAllRequests();
      reportService.cancelAllRequests();
    };
  }, [reportService, restService]);

  const storeTraceFile = useCallback(async (): Promise<string> => {
    const useAqueduct = isAqueductEnabled();
    const { url, formData, method } = await reportService.getUploadFormData();
    if (method === 'PUT') {
      await restService.put(url, file, { onUploadProgress: (p) => setUploadProgress(Math.round((p.progress || 0) * 100)), omitAuthHeader: useAqueduct });
      return formData.key;
    }
    await restService.post(
      url,
      {
        key: formData.key,
        acl: formData.acl,
        policy: formData.policy,
        'x-amz-meta-filename': file?.name,
        'x-amz-signature': formData['x-amz-signature'],
        'x-amz-credential': formData['x-amz-credential'],
        'x-amz-date': formData['x-amz-date'],
        'x-amz-algorithm': formData['x-amz-algorithm'],
        success_action_status: formData.success_action_status,
        'Content-Type': file?.name.endsWith('.gz') ? 'application/gzip' : '',
        file,
      },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.round((p.progress || 0) * 100)),
        omitAuthHeader: useAqueduct,
      }
    );
    return formData.key;
  }, [file, reportService, restService]);

  const uploadTrace = useCallback(
    async (file: File) => {
      setStatus(TraceUploadStatus.UPLOADING);
      setUploadProgress(0);
      setUploadError(null);
      try {
        // Upload the trace file to the bucket
        const key = await storeTraceFile();

        // End of the upload phase, we do not know how long it will take from here:
        // reset the upload progress to display the indeterminate progress ring
        setUploadProgress(null);

        // Create the report
        const report = await reportService.createReport(projectId, slotId, file.name, key, file.size);
        onDone(report);
      } catch (e: unknown) {
        setStatus(TraceUploadStatus.ERROR);
        setUploadError(getErrorMessage(e));
      }
    },
    [projectId, slotId, onDone, reportService, storeTraceFile]
  );

  const onFileRemove = () => {
    setFile(null);
  };

  const onFileRetry = () => {
    if (file) {
      uploadTrace(file);
    }
  };

  useEffect(() => {
    if (file && !fileError) {
      uploadTrace(file);
    } else {
      setStatus(TraceUploadStatus.READY);
    }
  }, [file, fileError, uploadTrace]);

  if (status === TraceUploadStatus.READY) {
    return (
      <Stack spacing={2}>
        {showCapacityWarning && <Alert severity="warning">{t(`traceUploadForm.maxReportsWarning`)}</Alert>}
        {fileComponent}
        {file && <FileEntry file={file} error={fileError} onRemove={onFileRemove} />}
      </Stack>
    );
  }

  if (status === TraceUploadStatus.UPLOADING) {
    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6,
            px: 0,
            border: '2px dashed',
            borderRadius: '4px',
            borderColor: 'text.disabled',
          }}
        >
          <ProgressRing progress={uploadProgress} />
          {uploadProgress !== null && <Typography variant="body1">{t('traceUploadForm.uploading')}</Typography>}
          {uploadProgress === null && <Typography variant="body1">{t('traceUploadForm.processing')}</Typography>}
        </Box>
        {file && <FileEntry file={file} />}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {fileComponent}
      {file && <FileEntry file={file} error={uploadError} onRemove={onFileRemove} onRetry={onFileRetry} />}
    </Stack>
  );
};
